import React, { useState } from "react";
import styled from "styled-components";
import { Search, Check, Globe } from "lucide-react";
import { lightThemeColors } from "../../assets/styles/colors";

const LanguagePicker = ({ languages, selected, onChange, title = "Select languages" }) => {
  const [search, setSearch] = useState("");

  const toggleLanguage = (name) => {
    onChange(
      selected.includes(name)
        ? selected.filter((l) => l !== name)
        : [...selected, name]
    );
  };

  const toggleSelectAll = () => {
    onChange(selected.length === languages.length ? [] : languages.map((l) => l.name));
  };

  const filtered = languages.filter(
    (l) =>
      l.name.toLowerCase().includes(search.toLowerCase()) ||
      l.translation.toLowerCase().includes(search.toLowerCase())
  );

  const allSelected = languages.length > 0 && selected.length === languages.length;
  const someSelected = selected.length > 0 && !allSelected;

  return (
    <Container>
      <Head>
        <HeadTitle>
          <Globe size={16} />
          {title}
        </HeadTitle>
        <Count $active={selected.length > 0}>
          {selected.length} of {languages.length} selected
        </Count>
      </Head>

      <SearchWrapper>
        <Search size={15} />
        <input
          type="text"
          placeholder="Search languages…"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </SearchWrapper>

      <SelectAllRow onClick={toggleSelectAll}>
        <CheckBox $checked={allSelected} $indeterminate={someSelected}>
          {allSelected && <Check size={12} />}
        </CheckBox>
        <span>Select all languages</span>
      </SelectAllRow>

      <LanguageGrid>
        {filtered.length === 0 ? (
          <Empty>No languages match "{search}".</Empty>
        ) : (
          filtered.map((lang) => {
            const isSelected = selected.includes(lang.name);
            return (
              <LanguageCard
                key={lang.name}
                $checked={isSelected}
                onClick={() => toggleLanguage(lang.name)}
              >
                <CheckBox $checked={isSelected}>
                  {isSelected && <Check size={12} />}
                </CheckBox>
                <LanguageInfo>
                  <LanguageName>{lang.name}</LanguageName>
                  <LanguageNative>{lang.translation}</LanguageNative>
                </LanguageInfo>
              </LanguageCard>
            );
          })
        )}
      </LanguageGrid>
    </Container>
  );
};

export default LanguagePicker;

const Container = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 10px;
  margin-bottom: 10px;
  border-bottom: 1px solid #e5e7eb;
`;

const HeadTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13.5px;
  font-weight: 700;
  color: ${lightThemeColors.darkColor};

  svg {
    color: ${lightThemeColors.primaryColor};
  }
`;

const Count = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${(p) => (p.$active ? lightThemeColors.primaryColor : "#98a2b3")};
  font-variant-numeric: tabular-nums;
`;

const SearchWrapper = styled.div`
  position: relative;
  margin-bottom: 10px;

  svg {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #98a2b3;
    pointer-events: none;
  }

  input {
    width: 100%;
    font-size: 13.5px;
    padding: 8px 12px 8px 31px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    outline: none;

    &:focus {
      border-color: ${lightThemeColors.primaryColor};
      box-shadow: 0 0 0 3px rgba(112, 105, 253, 0.15);
    }
  }
`;

const SelectAllRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  margin-bottom: 10px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;

  &:hover {
    background: #f4ebff;
  }

  span {
    font-size: 13px;
    font-weight: 600;
    color: ${lightThemeColors.darkColor};
  }
`;

const CheckBox = styled.span`
  flex-shrink: 0;
  width: 17px;
  height: 17px;
  border-radius: 5px;
  border: 1.5px solid ${(p) => (p.$checked || p.$indeterminate ? lightThemeColors.primaryColor : "#d1d5db")};
  background: ${(p) => (p.$checked || p.$indeterminate ? lightThemeColors.primaryColor : "white")};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  position: relative;

  ${(p) =>
    p.$indeterminate &&
    !p.$checked &&
    `
    &::after {
      content: "";
      position: absolute;
      width: 8px;
      height: 2px;
      background: white;
      border-radius: 1px;
    }
  `}
`;

const LanguageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 8px;
  max-height: 230px;
  overflow-y: auto;
  padding: 2px;
  margin: 0 -2px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;

const LanguageCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.$checked ? lightThemeColors.primaryColor : "#e5e7eb")};
  background: ${(p) => (p.$checked ? "#f4ebff" : "white")};
  min-width: 0;
  transition: border-color 0.12s, transform 0.12s, box-shadow 0.12s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  }
`;

const LanguageInfo = styled.div`
  min-width: 0;
  flex: 1;
`;

const LanguageName = styled.div`
  color: ${lightThemeColors.darkColor};
  font-weight: 600;
  font-size: 13.5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LanguageNative = styled.div`
  color: #667085;
  font-size: 12px;
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Empty = styled.div`
  grid-column: 1 / -1;
  padding: 20px 12px;
  text-align: center;
  color: #667085;
  font-size: 13px;
`;
